import React from "react";
import {
  UserCheck,
  BarChart3,
  Headphones,
  Target,
  Layers,
} from "lucide-react";

const REASONS = [
  {
    icon: UserCheck,
    title: "Trained and Managed Agents",
    desc: "Every agent is onboarded, coached, and monitored by a dedicated manager.",
  },
  {
    icon: BarChart3,
    title: "Weekly KPI Reports",
    desc: "Clear visibility into calls, contacts, appointments, and conversions.",
  },
  {
    icon: Headphones,
    title: "Call Monitoring & QA",
    desc: "Live and recorded call reviews ensure consistent quality and compliance.",
  },
  {
    icon: Target,
    title: "Performance-Focused Execution",
    desc: "Targets, dashboards, and feedback loops keep output moving in the right direction.",
  },
  {
    icon: Layers,
    title: "Flexible and Scalable Team Setup",
    desc: "Start with one agent, scale to a full team — change the shape as you grow.",
  },
];

const WhyChooseUs = () => {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      style={{
        position: "relative",
        padding: "96px 0 144px",
        background: "rgba(10,10,10,0.70)",
        overflow: "hidden",
      }}
    >
      {/* Decorative background */}
      <div style={{ position: "absolute", inset: 0 }}>
        <div
          style={{
            position: "absolute",
            top: "30%",
            right: "-5%",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            filter: "blur(130px)",
            background: "radial-gradient(circle, rgba(210,170,60,0.06) 0%, transparent 70%)",
            animation: "hero-orb-1 22s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "10%",
            left: "-5%",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            filter: "blur(100px)",
            background: "radial-gradient(circle, rgba(243,201,69,0.04) 0%, transparent 70%)",
            animation: "hero-orb-3 18s ease-in-out infinite",
          }}
        />
      </div>

      <div style={{ position: "relative", maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        {/* Section header */}
        <div style={{ maxWidth: "640px", marginBottom: "64px" }}>
          <div
            className="reveal"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "10px",
              letterSpacing: "0.4em",
              color: "rgba(210,170,60,0.8)",
              textTransform: "uppercase",
              marginBottom: "20px",
            }}
          >
            Why Choose Us
          </div>
          <h2
            className="reveal reveal-delay-1"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(36px, 5vw, 60px)",
              fontWeight: 300,
              lineHeight: 1.08,
              color: "#fff",
              letterSpacing: "0.02em",
            }}
          >
            Built around{" "}
            <em style={{ color: "#e8c45a", fontStyle: "italic", fontWeight: 400 }}>accountability</em>, not hype.
          </h2>
        </div>

        {/* Cards grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
          {REASONS.map((r, i) => (
            <div
              key={r.title}
              data-testid={`reason-card-${i}`}
              className="reveal"
              style={{
                padding: "36px 32px",
                background: "rgba(8,8,8,0.6)",
                border: "1px solid rgba(255,255,255,0.06)",
                transition: "all 0.35s ease",
                animationDelay: `${i * 80}ms`,
                cursor: "default",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(210,170,60,0.25)";
                e.currentTarget.style.background = "rgba(10,10,10,0.8)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
                e.currentTarget.style.background = "rgba(8,8,8,0.6)";
              }}
            >
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "24px",
                  transition: "border-color 0.35s",
                }}
              >
                <r.icon style={{ width: "20px", height: "20px", color: "#e8c45a" }} />
              </div>
              <h3
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "22px",
                  fontWeight: 400,
                  color: "#fff",
                  lineHeight: 1.25,
                  letterSpacing: "0.01em",
                  marginBottom: "12px",
                }}
              >
                {r.title}
              </h3>
              <p
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: "13px",
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.5)",
                  lineHeight: 1.7,
                  letterSpacing: "0.01em",
                }}
              >
                {r.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
